import { useEffect, useState } from "react";
import { trackConversion } from "./lib/metaPixel";

const REG_KEY = "cmm_last_registration";
const NOM_KEY = "cmm_last_nomination";

const ThankYouPage = () => {
  const [reg, setReg] = useState(null);
  const [nom, setNom] = useState(null);

  useEffect(() => {
    // Read the just-completed registration/nomination
    let rawReg = null, rawNom = null;
    try { rawReg = sessionStorage.getItem(REG_KEY); } catch { /* noop */ }
    try { rawNom = sessionStorage.getItem(NOM_KEY); } catch { /* noop */ }
    if (rawReg) {
      try { setReg(JSON.parse(rawReg)); } catch { /* noop */ }
    }
    if (rawNom) {
      try { setNom(JSON.parse(rawNom)); } catch { /* noop */ }
    }

    // Fire Meta Pixel CompleteRegistration + server-side CAPI (de-duplicated by event_id)
    // Gated on real payload AND one-shot guard (survives StrictMode + refresh).
    try {
      if (typeof window !== "undefined") {
        // Prefer nomination event if present; else delegate
        const payloadRaw = rawNom || rawReg;
        if (payloadRaw) {
          const fired = sessionStorage.getItem("cmm_cr_fired");
          if (fired !== payloadRaw) {
            const isNom = !!rawNom;
            const data = JSON.parse(payloadRaw);
            const personName = isNom ? (data?.nominee?.name || "") : (data?.name || "");
            const personEmail = isNom ? (data?.nominee?.email || "") : (data?.email || "");
            const personPhone = isNom ? (data?.nominee?.phone || "") : (data?.phone || "");
            const [first, ...rest] = String(personName).trim().split(/\s+/);
            trackConversion("CompleteRegistration", {
              event_id: isNom ? `nom_${data.id}` : `reg_${data.id}`,
              custom_data: {
                content_name: isNom ? "CMM 2026 - Award Nomination" : "CMM 2026 - Delegate Registration",
                status: true,
                num_items: isNom ? (data?.categories?.length || 1) : 1,
              },
              user_data: {
                email: personEmail,
                phone: personPhone,
                first_name: first || "",
                last_name: rest.join(" "),
                city: data?.city || "",
              },
            });
            sessionStorage.setItem("cmm_cr_fired", payloadRaw);
          }
        }
      }
    } catch {
      /* noop */
    }

    // SEO — thank-you pages shouldn't index
    document.title = "Thank you · Commodity Market Movers 2026";
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, follow");
  }, []);

  // Prefer nomination view if present; else delegate view
  const isNomination = !!nom;
  const name = isNomination ? (nom?.nominee?.name || "") : (reg?.name || "");
  const email = isNomination ? (nom?.nominee?.email || "") : (reg?.email || "");
  const id = isNomination ? (nom?.id || "") : (reg?.id || "");
  const ref = id.slice(0, 8).toUpperCase();
  const firstName = name ? name.split(" ")[0] : "you";
  const categories = isNomination ? (nom?.categories || []) : [];

  return (
    <div className="min-h-screen bg-[#050b14] text-white relative overflow-hidden" data-testid="thank-you-page">
      {/* Background blobs */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[620px] h-[620px] rounded-full bg-[#f5c518]/30 blur-[120px] opacity-60" />
        <div className="absolute top-40 -right-44 w-[460px] h-[460px] rounded-full bg-[#f5c518]/20 blur-[100px] opacity-50" />
        <div className="absolute -bottom-40 left-1/3 w-[440px] h-[440px] rounded-full bg-[#f5c518]/15 blur-[110px] opacity-40" />
      </div>

      {/* Slim top bar */}
      <header className="relative z-10 max-w-6xl mx-auto px-5 lg:px-8 py-6 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3" data-testid="thank-you-home">
          <img src="/assets/cmm-logo.svg" alt="Commodity Market Movers" className="h-9" />
        </a>
        <a href="/" className="text-sm text-white/55 hover:text-white border border-white/15 rounded-full px-4 py-2" data-testid="thank-you-back">
          ← Back to site
        </a>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-5 lg:px-8 pt-10 pb-24 lg:pt-20 lg:pb-32">
        {/* Confirmed pill */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0a8a3a]/20 border border-[#0a8a3a]/50 text-[#5fe39c] text-[0.7rem] font-bold tracking-[0.22em] uppercase">
          <span className="w-2 h-2 rounded-full bg-[#0a8a3a] animate-pulse" /> {isNomination ? "Nomination in · Complimentary" : "Seat reserved · Complimentary"}
        </div>

        {/* Big headline */}
        <h1
          className="font-display font-extrabold mt-6 leading-[1.02] uppercase"
          style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: "clamp(2.4rem, 6vw, 4.8rem)", letterSpacing: "-0.035em" }}
          data-testid="thank-you-headline"
        >
          Thank you, <br className="hidden sm:block" />
          <span className="italic font-light" style={{ color: "#f5c518", fontWeight: 300 }}>{firstName}.</span>
        </h1>

        <p className="text-white/65 text-lg lg:text-xl mt-5 max-w-2xl leading-relaxed">
          {isNomination ? (
            <>Your nomination is <b className="text-white">with the jury</b> for the <b className="text-white">Commodity Market Movers Awards 2026</b>. We'll see you at the gala dinner on <b className="text-[#f5c518]">1 October 2026</b> — win or not.</>
          ) : (
            <>Your seat at the <b className="text-white">Commodity Market Movers Roundtable &amp; Awards 2026</b> is locked in. The room comes alive on <b className="text-[#f5c518]">1 October 2026</b> — and we can't wait for you to be in it.</>
          )}
        </p>

        {/* Booking card */}
        <div className="mt-9 grid sm:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-5">
            <div className="text-[0.65rem] uppercase tracking-[0.22em] text-[#f5c518] font-bold">Date &amp; Time</div>
            <div className="font-display text-2xl font-extrabold mt-2 text-white" style={{ letterSpacing: "-0.025em" }}>1 October 2026</div>
            <div className="text-white/55 text-sm mt-1">3:30 PM onwards · IST</div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-5">
            <div className="text-[0.65rem] uppercase tracking-[0.22em] text-[#f5c518] font-bold">Venue</div>
            <div className="font-display text-2xl font-extrabold mt-2 text-white" style={{ letterSpacing: "-0.025em" }}>The Westin Mumbai Powai Lake</div>
            <div className="text-white/55 text-sm mt-1">Andheri East, Mumbai</div>
          </div>
        </div>

        {/* Reference + email */}
        {(ref || email) && (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-5" data-testid="thank-you-details">
            <div className="text-[0.65rem] uppercase tracking-[0.22em] text-white/55 font-bold">Your booking</div>
            <div className="mt-2 grid sm:grid-cols-3 gap-3 text-sm">
              {ref && <div><div className="text-white/45 text-[0.7rem] uppercase tracking-wider">Reference</div><div className="font-mono text-white mt-0.5">#{ref}</div></div>}
              {name && <div><div className="text-white/45 text-[0.7rem] uppercase tracking-wider">Name</div><div className="text-white mt-0.5">{name}</div></div>}
              {email && <div><div className="text-white/45 text-[0.7rem] uppercase tracking-wider">Confirmation sent to</div><div className="text-white mt-0.5 break-all">{email}</div></div>}
            </div>
          </div>
        )}

        {/* Categories (nomination) */}
        {isNomination && categories.length > 0 && (
          <div className="mt-4 rounded-2xl border border-[#f5c518]/30 bg-[#f5c518]/5 backdrop-blur-md p-5" data-testid="thank-you-categories">
            <div className="text-[0.65rem] uppercase tracking-[0.22em] text-[#f5c518] font-bold">Categories submitted ({categories.length})</div>
            <ul className="mt-2 space-y-1.5 text-white/80 text-[0.92rem]">
              {categories.map((c) => (
                <li key={c} className="flex items-start gap-2"><span className="text-[#f5c518] mt-0.5">★</span>{c}</li>
              ))}
            </ul>
          </div>
        )}

        {/* What's next */}
        <div className="mt-9">
          <div className="text-[0.65rem] uppercase tracking-[0.22em] text-[#f5c518] font-bold">What happens next</div>
          <ol className="mt-3 space-y-2.5 text-white/75 text-[0.97rem]">
            {isNomination ? (
              <>
                <li className="flex gap-3"><span className="shrink-0 w-6 h-6 rounded-full bg-[#f5c518]/20 border border-[#f5c518]/50 text-[#f5c518] text-xs font-bold flex items-center justify-center">1</span>Confirmation email arrives in your inbox (check Promotions / Spam if you don't see it).</li>
                <li className="flex gap-3"><span className="shrink-0 w-6 h-6 rounded-full bg-[#f5c518]/20 border border-[#f5c518]/50 text-[#f5c518] text-xs font-bold flex items-center justify-center">2</span>The jury reviews your submission across every selected category.</li>
                <li className="flex gap-3"><span className="shrink-0 w-6 h-6 rounded-full bg-[#f5c518]/20 border border-[#f5c518]/50 text-[#f5c518] text-xs font-bold flex items-center justify-center">3</span>On 1 October, walk in from 3:30 PM — your gala dinner seat is reserved, win or not.</li>
              </>
            ) : (
              <>
                <li className="flex gap-3"><span className="shrink-0 w-6 h-6 rounded-full bg-[#f5c518]/20 border border-[#f5c518]/50 text-[#f5c518] text-xs font-bold flex items-center justify-center">1</span>Confirmation email arrives in your inbox (check Promotions / Spam if you don't see it).</li>
                <li className="flex gap-3"><span className="shrink-0 w-6 h-6 rounded-full bg-[#f5c518]/20 border border-[#f5c518]/50 text-[#f5c518] text-xs font-bold flex items-center justify-center">2</span>A week before the event we'll send you the final agenda &amp; venue map.</li>
                <li className="flex gap-3"><span className="shrink-0 w-6 h-6 rounded-full bg-[#f5c518]/20 border border-[#f5c518]/50 text-[#f5c518] text-xs font-bold flex items-center justify-center">3</span>On 1 October, walk in any time after 3:30 PM. Your seat is reserved.</li>
              </>
            )}
          </ol>
          {!isNomination && (
            <p className="text-white/50 text-xs italic mt-4">Note — your complimentary pass covers the sessions, keynote, awards &amp; networking. The gala dinner is reserved for award nominees &amp; sponsors.</p>
          )}
          {isNomination && (
            <p className="text-white/50 text-xs italic mt-4">Note — every nomination includes a seat at the gala dinner, whether the nomination wins or not.</p>
          )}
        </div>

        {/* Nomination upsell — only for delegates */}
        {!isNomination && (
        <div className="mt-10 rounded-2xl p-6 lg:p-7 relative overflow-hidden" style={{ background: "linear-gradient(120deg, #f5c518 0%, #f5c518 60%, #ffd95a 100%)" }} data-testid="thank-you-upsell">
          <div className="text-[0.65rem] uppercase tracking-[0.22em] text-[#050b14]/70 font-bold">Want the gala dinner too?</div>
          <div className="font-display text-2xl lg:text-3xl font-extrabold text-[#050b14] mt-2 leading-tight" style={{ letterSpacing: "-0.025em" }}>
            Put your name in for one of the 17 honours.
          </div>
          <p className="text-[#050b14]/75 text-sm mt-2 max-w-md">Every nomination includes a seat at the gala dinner and full jury consideration — yours, win or not. Now complimentary for the 2026 edition.</p>
          <a
            href="/#awards"
            className="inline-flex items-center px-5 py-3 mt-5 rounded-full bg-[#050b14] text-[#f5c518] font-extrabold text-sm uppercase tracking-wider hover:bg-[#0a1326]"
            data-testid="thank-you-nominate"
          >
            See the 17 honours →
          </a>
        </div>
        )}

        {/* Share row */}
        <div className="mt-10 flex flex-wrap items-center gap-3 text-sm text-white/55">
          <span className="text-[0.65rem] uppercase tracking-[0.22em] text-white/40 font-bold mr-2">Share with a peer:</span>
          <a
            href={`https://wa.me/?text=${encodeURIComponent("I just registered for the Commodity Market Movers Roundtable & Awards 2026 — 1 October, Mumbai. Free seats are still open: https://commoditymarketmovers.com")}`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-[#25d366]/50 hover:text-white"
            data-testid="thank-you-share-wa"
          >
            WhatsApp
          </a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent("https://commoditymarketmovers.com")}`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-[#0a66c2]/60 hover:text-white"
            data-testid="thank-you-share-li"
          >
            LinkedIn
          </a>
          <a
            href={`mailto:?subject=${encodeURIComponent("Commodity Market Movers 2026")}&body=${encodeURIComponent("I just registered — 1 October, Mumbai. Free seats are still open: https://commoditymarketmovers.com")}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-[#f5c518]/60 hover:text-white"
            data-testid="thank-you-share-email"
          >
            Email
          </a>
        </div>
      </main>

      <footer className="relative z-10 border-t border-white/10 py-6 text-center text-xs text-white/40">
        Convened by Tefla's · events@commoditymarketmovers.com · +91 98338 55566
      </footer>
    </div>
  );
};

export default ThankYouPage;
